import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLocationSelect } from "@/hooks/useLocationSelect";

import Header from "./components/Header";
import CategoryTabs from "./components/CategoryTabs";
import SortBar, { SORT_VALUE_MAP } from "./components/SortBar";
import ReplaceModeTip from "./components/ReplaceModeTip";
import BrandSidebar from "./components/BrandSidebar";
import CarList from "./components/CarList";
import ConfirmModal from "./components/ConfirmModal";

export default function CarListPage() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const isReplaceMode =
    (routerLocation.state as { replace?: boolean })?.replace === true;

  // 使用 hook 获取位置信息
  const { coords, locationDisplay, loading: locationLoading } = useLocationSelect();

  // 坐标是否已就绪（用于防止首次渲染时 coords 为空导致发无经纬度请求）
  const coordsReady = !locationLoading && !!(coords.latitude && coords.longitude);

  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [activeBrand, setActiveBrand] = useState(0);
  const [activeCategory, setActiveCategory] = useState(0);
  const [activeSort, setActiveSort] = useState(0);
  const handleLocationClick = () => {
    navigate("/location-select", { state: { from: "/cars" } });
  };

  const handleSelect = (id: number) => {
    if (isReplaceMode) {
      setSelectedId(id);
      setShowConfirm(true);
    } else {
      navigate("/car-detail-product", { state: { carId: id } });
    }
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    navigate("/ai-plan");
  };

  return (
    <div
      className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+80px)]"
      style={{
        background: "#F8F9FC",
        fontFamily: "'Inter','SF Pro Text',sans-serif",
      }}
    >
      {/* 固定顶部区域：Header + CategoryTabs + SortBar */}
      <div
        className="fixed top-0 left-0 right-0 z-50"
        style={{ background: "#F8F9FC" }}
      >
        {/* Header */}
        <Header
          currentLocation={locationDisplay}
          onLocationClick={handleLocationClick}
        />

        {/* Category Tabs */}
        <CategoryTabs
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
        />

        {/* Sort Bar */}
        <SortBar activeSort={activeSort} onSortChange={setActiveSort} />
      </div>

      {/* 占位区域，防止内容被固定头部遮挡 (Header + CategoryTabs + SortBar 总高度) */}
      <div style={{ height: 200 }} />

      {/* Replace Mode Tip */}
      <ReplaceModeTip isReplaceMode={isReplaceMode} />
      {/* Main Content */}
      <div className="flex items-start" style={{ minHeight: 400 }}>
        <BrandSidebar
          activeBrand={activeBrand}
          onBrandChange={setActiveBrand}
        />
        {coordsReady ? (
          <CarList
            activeBrand={activeBrand}
            activeCategory={activeCategory}
            sort={SORT_VALUE_MAP[activeSort]}
            coords={coords}
            isReplaceMode={isReplaceMode}
            onSelect={handleSelect}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center py-20">
            <span className="text-sm text-gray-400">正在获取位置信息...</span>
          </div>
        )}
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        show={showConfirm}
        selectedId={selectedId}
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
      />
    </div>
  );
}
